// frontend/src/marketing/pages/DocsIndexPage.tsx
//
// Help center index page — lists all published docs grouped by category
// fetched from /api/public/site/docs.

import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, FileText, ChevronRight } from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';
import { getHelpDocs, type PublicHelpDoc } from '../api';

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

export function DocsIndexPage() {
  const [docs, setDocs] = React.useState<PublicHelpDoc[] | null>(null);
  const [activeCategory, setActiveCategory] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    getHelpDocs()
      .then((d) => { if (!cancelled) setDocs(d); })
      .catch(() => { if (!cancelled) setDocs([]); });
    return () => { cancelled = true; };
  }, []);

  // Group docs by category preserving service ordering (category ASC, sort_order ASC, title ASC).
  const grouped = React.useMemo<Map<string, PublicHelpDoc[]>>(() => {
    if (!docs) return new Map();
    const map = new Map<string, PublicHelpDoc[]>();
    for (const doc of docs) {
      const cat = doc.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(doc);
    }
    return map;
  }, [docs]);

  const categories = React.useMemo(() => Array.from(grouped.keys()), [grouped]);

  // Default to first category when docs load.
  React.useEffect(() => {
    if (categories.length > 0 && activeCategory === null) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Help Center"
        description="Guides, FAQs, and documentation to help you get the most out of Worqly."
      />

      <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={STAGGER}>
            <motion.div variants={FADE_UP}>
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                  bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                  dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
              >
                <BookOpen className="h-3 w-3 text-coral" />
                Documentation
              </span>
            </motion.div>
            <motion.h1
              variants={FADE_UP}
              className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
            >
              Guides and <span className="italic font-serif text-coral">answers.</span>
            </motion.h1>
            <motion.p
              variants={FADE_UP}
              className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
            >
              Everything you need to get set up, run payroll, send invoices, and grow your business with Worqly.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          {docs === null ? (
            <Skeleton />
          ) : docs.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Category sidebar */}
              <aside className="lg:w-56 shrink-0">
                <nav aria-label="Doc categories">
                  <ul className="space-y-1">
                    {categories.map((cat) => (
                      <li key={cat}>
                        <button
                          type="button"
                          onClick={() => setActiveCategory(cat)}
                          className={
                            'w-full text-left flex items-center gap-2 px-3.5 py-2.5 rounded-2xl text-[13.5px] font-semibold transition-colors ' +
                            (activeCategory === cat
                              ? 'bg-coral text-white shadow-cta'
                              : 'text-ink/70 dark:text-white/70 hover:bg-white/60 dark:hover:bg-white/10 hover:text-ink dark:hover:text-white')
                          }
                        >
                          <BookOpen className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{cat}</span>
                          <span className={
                            'ml-auto text-[11px] font-bold tabular-nums ' +
                            (activeCategory === cat ? 'text-white/80' : 'text-ink/45 dark:text-white/40')
                          }>
                            {grouped.get(cat)?.length ?? 0}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </aside>

              {/* Article list */}
              <div className="flex-1 min-w-0">
                {activeCategory && grouped.has(activeCategory) && (
                  <motion.div
                    key={activeCategory}
                    initial="hidden"
                    animate="visible"
                    variants={STAGGER}
                  >
                    <motion.p variants={FADE_UP} className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/50 dark:text-white/50 mb-4">
                      {activeCategory}
                    </motion.p>
                    <ul className="space-y-2.5">
                      {grouped.get(activeCategory)!.map((doc) => (
                        <motion.li key={doc.id} variants={FADE_UP}>
                          <Link
                            to={`/docs/${doc.slug}`}
                            className="group flex items-center gap-4 rounded-2xl p-5 transition-all bg-white/85 ring-1 ring-ink/5 shadow-card hover:shadow-card-lg dark:bg-white/[0.04] dark:ring-white/10"
                          >
                            <div className="h-9 w-9 rounded-xl bg-coral/10 dark:bg-coral/15 flex items-center justify-center shrink-0">
                              <FileText className="h-4 w-4 text-coral" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-semibold text-ink dark:text-white tracking-[-0.005em] group-hover:text-coral dark:group-hover:text-coral-400 transition-colors">
                                {doc.title}
                              </p>
                              {doc.excerpt && (
                                <p className="mt-0.5 text-[13px] text-ink/60 dark:text-white/60 line-clamp-1">
                                  {doc.excerpt}
                                </p>
                              )}
                            </div>
                            <ChevronRight className="h-4 w-4 text-ink/35 dark:text-white/35 group-hover:text-coral transition-colors shrink-0" />
                          </Link>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <FooterCTA />
    </div>
  );
}

function Skeleton() {
  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <div className="lg:w-56 shrink-0 space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 rounded-2xl bg-ink/10 dark:bg-white/10 animate-pulse" />
        ))}
      </div>
      <ul className="flex-1 space-y-2.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <li
            key={i}
            className="rounded-2xl p-5 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card flex items-center gap-4"
          >
            <div className="h-9 w-9 rounded-xl bg-ink/10 dark:bg-white/10 animate-pulse shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-2/3 rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
              <div className="h-3 w-full rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl p-10 lg:p-14 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card text-center max-w-xl mx-auto">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 mb-5">
        <BookOpen className="h-5 w-5" />
      </span>
      <Eyebrow className="!text-coral">Soon</Eyebrow>
      <h2 className="mt-2 text-[22px] font-semibold text-ink dark:text-white tracking-[-0.005em]">
        Documentation is being written.
      </h2>
      <p className="mt-3 text-[14px] text-ink/65 dark:text-white/65 leading-relaxed">
        Our team is preparing guides. Check the{' '}
        <Link to="/changelog" className="text-coral font-semibold hover:underline">changelog</Link>{' '}
        for the latest updates in the meantime.
      </p>
    </div>
  );
}

function FooterCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">Start free</p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Ready to try <em className="font-serif">Worqly?</em>
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Seven days free, no card to start. Pick the modules you actually need.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                See pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
